'use client';

import { useEffect, useRef, useState } from 'react';

type NoiseType = 'white' | 'pink' | 'brown';

const NOISE_OPTIONS: { value: NoiseType; label: string }[] = [
  { value: 'white', label: 'White noise' },
  { value: 'pink', label: 'Pink noise' },
  { value: 'brown', label: 'Brown noise' },
];

function fillNoiseBuffer(buffer: AudioBuffer, type: NoiseType) {
  const data = buffer.getChannelData(0);
  const length = data.length;

  if (type === 'white') {
    for (let i = 0; i < length; i += 1) {
      data[i] = Math.random() * 2 - 1;
    }
    return;
  }

  if (type === 'pink') {
    let b0 = 0;
    let b1 = 0;
    let b2 = 0;
    let b3 = 0;
    let b4 = 0;
    let b5 = 0;
    let b6 = 0;
    for (let i = 0; i < length; i += 1) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.969 * b2 + white * 0.153852;
      b3 = 0.8665 * b3 + white * 0.3104856;
      b4 = 0.55 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.016898;
      const value = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
      b6 = white * 0.115926;
      data[i] = value * 0.11;
    }
    return;
  }

  // brown
  let lastOut = 0;
  for (let i = 0; i < length; i += 1) {
    const white = Math.random() * 2 - 1;
    lastOut = (lastOut + 0.02 * white) / 1.02;
    data[i] = lastOut * 3.5;
  }
}

export default function BrowserWhiteNoiseGenerator() {
  const [noiseType, setNoiseType] = useState<NoiseType>('white');
  const [volume, setVolume] = useState(0.4);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const volumeRef = useRef(volume);
  const noiseTypeRef = useRef(noiseType);

  useEffect(() => {
    volumeRef.current = volume;
    if (gainRef.current && audioContextRef.current) {
      gainRef.current.gain.setTargetAtTime(volume, audioContextRef.current.currentTime, 0.02);
    }
  }, [volume]);

  useEffect(() => {
    noiseTypeRef.current = noiseType;
  }, [noiseType]);

  const stopSource = () => {
    if (sourceRef.current) {
      try {
        sourceRef.current.stop();
      } catch {
        // already stopped
      }
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopSource();
      if (audioContextRef.current) {
        void audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  function startSource(context: AudioContext) {
    stopSource();
    const bufferSeconds = 3;
    const buffer = context.createBuffer(1, context.sampleRate * bufferSeconds, context.sampleRate);
    fillNoiseBuffer(buffer, noiseTypeRef.current);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gainRef.current as GainNode);
    source.start();
    sourceRef.current = source;
  }

  async function play() {
    if (typeof window === 'undefined') {
      return;
    }
    setError(null);
    try {
      let context = audioContextRef.current;
      if (!context) {
        const AudioCtx =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioCtx) {
          setError('Your browser does not support the Web Audio API.');
          return;
        }
        context = new AudioCtx();
        audioContextRef.current = context;
        const gain = context.createGain();
        gain.gain.value = volumeRef.current;
        gain.connect(context.destination);
        gainRef.current = gain;
      }
      if (context.state === 'suspended') {
        await context.resume();
      }
      startSource(context);
      setIsPlaying(true);
    } catch {
      setError('The browser could not start audio playback.');
    }
  }

  function stop() {
    stopSource();
    setIsPlaying(false);
  }

  function toggle() {
    if (isPlaying) {
      stop();
    } else {
      void play();
    }
  }

  function handleNoiseChange(next: NoiseType) {
    setNoiseType(next);
    noiseTypeRef.current = next;
    if (isPlaying && audioContextRef.current) {
      startSource(audioContextRef.current);
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">White Noise Generator</span>
        <h2 className="tool-live-title">White, pink &amp; brown noise generator</h2>
        <p className="tool-live-subtitle">
          Play soothing white, pink, or brown noise to help you focus, relax, or sleep. Choose a
          noise type, set the volume, and press play. Everything runs in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Noise type</span>
              <select
                onChange={(event) => handleNoiseChange(event.target.value as NoiseType)}
                value={noiseType}
              >
                {NOISE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Volume: {Math.round(volume * 100)}%</span>
              <input
                max={1}
                min={0}
                onChange={(event) => setVolume(Number(event.target.value))}
                step={0.01}
                type="range"
                value={volume}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={toggle} type="button">
              {isPlaying ? 'Stop' : 'Play'}
            </button>
          </div>
          {error ? <p className="status-message status-error">{error}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              minHeight: '11rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              padding: '1.5rem',
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: '2.4rem' }} aria-hidden="true">
              {isPlaying ? '🔊' : '🔈'}
            </span>
            <span style={{ fontSize: '1.15rem', fontWeight: 600 }}>
              {isPlaying
                ? `Playing ${NOISE_OPTIONS.find((o) => o.value === noiseType)?.label}`
                : 'Stopped'}
            </span>
          </div>
          <p className="tool-live-panel-note">
            White noise is even across frequencies, pink noise is softer and warmer, and brown noise
            emphasises the low end. Keep the volume comfortable, especially with headphones.
          </p>
        </section>
      </div>
    </div>
  );
}
